import { useCallback, useMemo } from 'react';
import { Shot, ScriptTone, DragHandlers } from '../types';

interface UseSceneCardHandlersProps {
  shot: Shot;
  onUpdateShot: (updates: Partial<Shot>) => void;
  onSaveShot: () => Promise<void>;
  onDeleteShot: () => void;
  onDuplicateShot: () => void;
  hasDeletePermission: boolean;
  dragHandlers?: DragHandlers;
}

export function useSceneCardHandlers({
  shot,
  onUpdateShot,
  onSaveShot,
  onDeleteShot,
  onDuplicateShot,
  hasDeletePermission,
  dragHandlers
}: UseSceneCardHandlersProps) {
  const sceneId = shot.shotId || shot.tempId || '';

  const handleFieldBlur = useCallback(
    (fieldName: string) => async ({ value }: { value: string }) => {
      onUpdateShot({ [fieldName]: value });
      await onSaveShot();
    },
    [onUpdateShot, onSaveShot]
  );

  const handleScriptToneChange = useCallback(
    async ({ value }: { value: string }) => {
      onUpdateShot({ scriptTone: value as ScriptTone | '' });
      await onSaveShot();
    },
    [onUpdateShot, onSaveShot]
  );

  const handleTitleBlur = useCallback(
    async ({ value }: { value: string }) => {
      onUpdateShot({ name: value });
      await onSaveShot();
    },
    [onUpdateShot, onSaveShot]
  );

  const handleAssetChange = useCallback(
    (assetId: string) => onUpdateShot({ assetId }),
    [onUpdateShot]
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      const sceneCard = e.currentTarget.closest('[data-scene-card]') as HTMLElement;
      e.dataTransfer.setData('sceneId', String(sceneId));

      if (sceneCard && dragHandlers) {
        dragHandlers.onDragStart({ ...e, currentTarget: sceneCard, target: sceneCard } as React.DragEvent);
      } else {
        dragHandlers?.onDragStart(e);
      }
    },
    [sceneId, dragHandlers]
  );

  const moreActionsOptions = useMemo(
    () => [
      { label: 'Duplicate', action: onDuplicateShot },
      { label: 'Delete', action: hasDeletePermission ? onDeleteShot : undefined, isRed: true, disabled: !hasDeletePermission }
    ],
    [onDuplicateShot, onDeleteShot, hasDeletePermission]
  );

  return {
    sceneId,
    handleFieldBlur,
    handleScriptToneChange,
    handleTitleBlur,
    handleAssetChange,
    handleDragStart,
    moreActionsOptions
  };
}
